<template>
  <div class="equipment-page">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="container">
        <h1 class="page-title">Accessibility Equipment</h1>
        <p class="page-subtitle">Professional accessibility solutions for diverse needs</p>
      </div>
    </div>

    <!-- 设备分类网格 -->
    <div class="equipment-section">
      <div class="container">
        <div class="equipment-grid">
          <div 
            v-for="category in equipmentCategories" 
            :key="category.id" 
            class="category-card"
            @click="navigateToCategory(category)"
          >
            <div class="category-image">
              <div class="category-icon">{{ category.icon }}</div>
            </div>
            <div class="category-info">
              <h3 class="category-name">{{ category.name }}</h3>
              <p class="category-desc">{{ category.description }}</p>
              <div class="category-stats">
                <span class="stat">{{ category.productCount }} products</span>
                <span class="stat">{{ category.priceRange }}</span>
              </div>
              <div class="category-features">
                <span v-for="feature in category.features" :key="feature" class="feature-tag">
                  {{ feature }}
                </span>
              </div>
            </div>
            <div class="category-arrow">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Equipment',
  data() {
    return {
      equipmentCategories: [
        {
          id: 'wheelchairs',
          name: 'Wheelchair Series',
          description: 'Electric, manual, sports and other wheelchair solutions',
          icon: '🦽',
          productCount: 8,
          priceRange: '¥5,000-50,000',
          features: ['Electric Wheelchairs', 'Manual Wheelchairs', 'Sports Wheelchairs', 'Care Wheelchairs'],
          route: '/wheelchairs'
        },
        {
          id: 'cushions',
          name: 'Cushion Accessories',
          description: 'Memory foam, gel and other comfortable cushions to enhance wheelchair experience',
          icon: '🪑',
          productCount: 12,
          priceRange: '¥280-5,500',
          features: ['Anti-Pressure Cushions', 'Gel Cushions', 'Memory Foam Cushions', 'Children Cushions'],
          route: '/products'
        },
        {
          id: 'walkers',
          name: 'Walkers',
          description: 'Safe and reliable walking aids for people with mobility difficulties',
          icon: '🦯',
          productCount: 8,
          priceRange: '¥800-3,000',
          features: ['Four-Wheel Walkers', 'Two-Wheel Walkers', 'Foldable', 'With Seat'],
          route: '/walkers'
        },
        {
          id: 'prosthetics',
          name: 'Prosthetics & Orthotics',
          description: 'Professional prosthetic and orthotic customization services',
          icon: '🦿',
          productCount: 15,
          priceRange: '¥2,000-20,000',
          features: ['Upper Limb Prosthetics', 'Lower Limb Prosthetics', 'Orthotics', 'Custom Services'],
          route: '/prosthetics'
        },
        {
          id: 'bathroom',
          name: 'Bathroom Equipment',
          description: 'Safe and comfortable bathroom assistive devices',
          icon: '🚿',
          productCount: 10,
          priceRange: '¥500-5,000',
          features: ['Toilet Seats', 'Shower Chairs', 'Grab Bars', 'Non-Slip Mats'],
          route: '/bathroom'
        },
        {
          id: 'beds',
          name: 'Care Beds',
          description: 'Professional medical care beds and mattresses',
          icon: '🛏️',
          productCount: 6,
          priceRange: '¥3,000-15,000',
          features: ['Electric Care Beds', 'Manual Care Beds', 'Anti-Pressure Mattresses', 'Bed Rails'],
          route: '/beds'
        }
      ]
    }
  },
  methods: {
    navigateToCategory(category) {
      this.$router.push(category.route);
    }
  }
}
</script>

<style scoped>
.equipment-page {
  min-height: 100vh;
  background: #f8f9fa;
}

.page-header {
  background: linear-gradient(135deg, #2c5aa0 0%, #4a7bc8 100%);
  color: white;
  padding: 4rem 2rem;
  text-align: center;
}

.page-title {
  font-size: 3rem;
  margin-bottom: 1rem;
  font-weight: 300;
}

.page-subtitle {
  font-size: 1.2rem;
  opacity: 0.9;
}

.equipment-section {
  padding: 3rem 2rem;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
}

.equipment-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(380px, 1fr));
  gap: 2rem;
}

.category-card {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  overflow: hidden;
}

.category-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 30px rgba(0,0,0,0.12);
}

.category-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(135deg, #2c5aa0, #4a7bc8);
  transform: scaleX(0);
  transition: transform 0.3s ease;
}

.category-card:hover::before {
  transform: scaleX(1);
}

.category-image {
  display: flex;
  justify-content: center;
  margin-bottom: 1.5rem;
}

.category-icon {
  font-size: 3rem;
  background: linear-gradient(135deg, #2c5aa0, #4a7bc8);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.category-info {
  text-align: center;
}

.category-name {
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
  color: #333;
  font-weight: 600;
}

.category-desc {
  color: #666;
  margin-bottom: 1rem;
  line-height: 1.5;
}

.category-stats {
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 1rem;
}

.stat {
  background: #e3f2fd;
  color: #2c5aa0;
  padding: 0.3rem 0.8rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 500;
}

.category-features {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: center;
  margin-bottom: 1rem;
}

.feature-tag {
  background: #f8f9fa;
  color: #666;
  padding: 0.2rem 0.6rem;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 500;
}

.category-arrow {
  position: absolute;
  top: 1rem;
  right: 1rem;
  color: #2c5aa0;
  opacity: 0;
  transition: all 0.3s ease;
}

.category-card:hover .category-arrow {
  opacity: 1;
  transform: translateX(5px);
}

@media (max-width: 768px) {
  .page-title {
    font-size: 2rem;
  }
  
  .equipment-grid {
    grid-template-columns: 1fr;
  }
  
  .category-card {
    padding: 1.5rem;
  }
  
  .category-icon {
    font-size: 2.5rem;
  }
}
</style> 